import React, { useState, useEffect } from 'react';
import QuestionForm from '../CreateQuiz/QuestionForm';
import QuestionImport from '../Common/QuestionImport';
import AIQuizGenerator from '../Common/AIQuizGenerator';
import { useAuth } from '../Auth/AuthProvider';
import { 
  getQuiz, 
  updateQuiz, 
  getQuizQuestions, 
  addQuestionToQuiz, 
  updateQuestion, 
  deleteQuestion,
  importQuestionsToQuiz,
  deleteQuiz 
} from '../../firebase';
import './EditQuizForm.css';

const EditQuizForm = ({ quizId, onSave, onCancel, onDelete }) => {
  const { user } = useAuth();
  const [quiz, setQuiz] = useState({
    title: '',
    description: '',
    visibility: 'private',
    published: false
  });
  const [publishMode, setPublishMode] = useState('private'); // 'private' | 'limited' | 'public'
  const [questions, setQuestions] = useState([]);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [aiGeneratedJson, setAiGeneratedJson] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('');
  const questionsPerPage = 5;

  useEffect(() => {
    const loadQuizData = async () => {
      try {
        setLoading(true);
        
        // Load quiz basic info
        const quizData = await getQuiz(user.uid, quizId);
        setQuiz({
          title: quizData.title,
          description: quizData.description,
          visibility: quizData.visibility,
          published: quizData.published
        });
        // 既存データから公開モードを導出
        if (quizData.visibility === 'private') {
          setPublishMode('private');
        } else if (quizData.visibility === 'public' && quizData.published === true) {
          setPublishMode('public');
        } else if (quizData.visibility === 'public' && quizData.published === false) {
          setPublishMode('limited');
        } else {
          // 想定外はprivateにフェールバック
          setPublishMode('private');
        }

        // Load questions
        const questionsData = await getQuizQuestions(user.uid, quizId);
        setQuestions(questionsData);
      } catch (error) {
        console.error('Failed to load quiz:', error);
        alert('クイズの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    if (user && quizId) {
      loadQuizData();
    }
  }, [user, quizId]);

  const handleQuizChange = (field, value) => {
    setQuiz(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 公開モード変更時に quiz.visibility / quiz.published を同期
  const handlePublishModeChange = (mode) => {
    setPublishMode(mode);
    if (mode === 'private') {
      setQuiz(prev => ({ ...prev, visibility: 'private', published: false }));
    } else if (mode === 'limited') {
      // 限定公開: visibility = 'public', published = false
      setQuiz(prev => ({ ...prev, visibility: 'public', published: false }));
    } else if (mode === 'public') {
      setQuiz(prev => ({ ...prev, visibility: 'public', published: true }));
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestionIndex(null);
    setEditingQuestionId(null);
    setShowQuestionForm(true);
  };

  const handleImportQuestions = () => {
    setShowImportForm(true);
  };

  const handleAiQuizGeneration = () => {
    setShowAiGenerator(true);
  };


  const handleImportSubmit = async (questionsData) => {
    try {
      const importedQuestionIds = await importQuestionsToQuiz(user.uid, quizId, questionsData);
      
      // Add imported questions to local state
      const newQuestions = questionsData.map((questionData, index) => ({
        id: importedQuestionIds[index],
        ...questionData
      }));
      
      setQuestions(prev => [...prev, ...newQuestions]);
      setShowImportForm(false);
      
      alert(`${questionsData.length}個の質問をインポートしました！`);
    } catch (error) {
      console.error('Failed to import questions:', error);
      alert('質問のインポートに失敗しました');
    }
  };

  const handleAiGenerateResult = (result) => {
    try {
      // Convert AI result to JSON string and show in import form
      const jsonString = JSON.stringify(result, null, 2);
      setAiGeneratedJson(jsonString);
      setShowAiGenerator(false);
      setShowImportForm(true);
    } catch (error) {
      console.error('Failed to process AI generated questions:', error);
      alert('AI生成されたクイズの処理に失敗しました');
    }
  };

  const handleEditQuestion = (index, questionId) => {
    setEditingQuestionIndex(index);
    setEditingQuestionId(questionId);
    setShowQuestionForm(true);
  };

  const handleSaveQuestion = async (questionData) => {
    try {
      if (editingQuestionIndex !== null && editingQuestionId) {
        // Update existing question
        await updateQuestion(user.uid, quizId, editingQuestionId, {
          ...questionData,
          order: editingQuestionIndex + 1
        });
        
        setQuestions(prev => prev.map((q, i) => 
          i === editingQuestionIndex ? { ...q, ...questionData } : q
        ));
      } else {
        // Add new question
        const newQuestionId = await addQuestionToQuiz(user.uid, quizId, {
          ...questionData,
          order: questions.length + 1
        });
        
        setQuestions(prev => [...prev, { 
          id: newQuestionId, 
          ...questionData,
          order: questions.length + 1
        }]);
      }
      
      setShowQuestionForm(false);
      setEditingQuestionIndex(null);
      setEditingQuestionId(null);
    } catch (error) {
      console.error('Failed to save question:', error);
      alert('質問の保存に失敗しました');
    }
  };

  const handleDeleteQuestion = async (index, questionId) => {
    if (window.confirm('この質問を削除しますか？')) {
      try {
        await deleteQuestion(user.uid, quizId, questionId);
        setQuestions(prev => prev.filter((_, i) => i !== index));
        
        // If current page becomes empty after deletion, go to previous page
        const newTotalPages = Math.ceil((questions.length - 1) / questionsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }
      } catch (error) {
        console.error('Failed to delete question:', error);
        alert('質問の削除に失敗しました');
      }
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = questions.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const pageNum = parseInt(pageInput);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setPageInput('');
    } else {
      alert(`1から${totalPages}までの数値を入力してください`);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!quiz.title.trim()) {
      alert('クイズのタイトルを入力してください');
      return;
    }

    setSaving(true);
    try {
      await updateQuiz(user.uid, quizId, quiz);
      alert('クイズが更新されました！');
      onSave();
    } catch (error) {
      console.error('Failed to update quiz:', error);
      alert('クイズの更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuiz = async () => {
    try {
      console.log('Deleting quiz:', {
        userId: user.uid,
        quizId: quizId,
        userAuth: user ? 'authenticated' : 'not authenticated'
      });
      
      await deleteQuiz(user.uid, quizId);
      alert('クイズが削除されました');
      setShowDeleteConfirm(false);
      onDelete();
    } catch (error) {
      console.error('Failed to delete quiz:', error);
      console.error('Error details:', {
        userId: user.uid,
        quizId: quizId,
        errorMessage: error.message,
        errorCode: error.code,
        errorStack: error.stack
      });
      
      // Show more specific error message
      let errorMsg = 'クイズの削除に失敗しました';
      if (error.code === 'permission-denied') {
        errorMsg = 'このクイズを削除する権限がありません';
      } else if (error.message) {
        errorMsg = `削除に失敗しました: ${error.message}`;
      }
      
      alert(errorMsg);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-quiz-form">
        <div className="loading">クイズを読み込み中...</div>
      </div>
    );
  }

  if (showQuestionForm) {
    const questionToEdit = editingQuestionIndex !== null ? questions[editingQuestionIndex] : null;
    
    return (
      <QuestionForm
        question={questionToEdit}
        onSave={handleSaveQuestion}
        onCancel={() => {
          setShowQuestionForm(false);
          setEditingQuestionIndex(null);
          setEditingQuestionId(null);
        }}
      />
    );
  }

  if (showImportForm) {
    return (
      <QuestionImport
        onImport={handleImportSubmit}
        onCancel={() => {
          setShowImportForm(false);
          setAiGeneratedJson('');
        }}
        initialJsonData={aiGeneratedJson}
      />
    );
  }

  if (showAiGenerator) {
    return (
      <AIQuizGenerator
        onGenerate={handleAiGenerateResult}
        onCancel={() => setShowAiGenerator(false)}
      />
    );
  }

  return (
    <div className="edit-quiz-form">
      <div className="edit-quiz-form-header">
        <h2>クイズを編集</h2>
        <button onClick={onCancel} className="cancel-btn">
          ← 戻る
        </button>
      </div>

      <form onSubmit={handleSubmit} className="quiz-details-form">
        <div className="form-group">
          <label>クイズタイトル *</label>
          <input
            type="text"
            value={quiz.title}
            onChange={(e) => handleQuizChange('title', e.target.value)}
            placeholder="クイズのタイトルを入力"
            required
          />
        </div>

        <div className="form-group">
          <label>説明</label>
          <textarea
            value={quiz.description}
            onChange={(e) => handleQuizChange('description', e.target.value)}
            placeholder="クイズの説明を入力"
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>公開設定</label>
          <select
            value={publishMode}
            onChange={(e) => handlePublishModeChange(e.target.value)}
          >
            <option value="private">非公開</option>
            <option value="limited">限定公開（URL を知っている人のみ）</option>
            <option value="public">一般公開（一覧に表示）</option>
          </select>
          {quiz.visibility === 'public' && (
            <div className="share-url-section">
              <div className="share-url-info">
                <label>公開URL:</label>
                <div className="share-url-container">
                  <input
                    type="text"
                    value={`${window.location.origin}/shared-quiz/${user.uid}/${quizId}?skip-settings=true`}
                    readOnly
                    className="share-url-input"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const shareUrl = `${window.location.origin}/shared-quiz/${user.uid}/${quizId}?skip-settings=true`;
                      navigator.clipboard.writeText(shareUrl);
                      alert('共有URLをコピーしました！');
                    }}
                    className="copy-url-btn"
                  >
                    コピー
                  </button>
                </div>
                <p className="share-url-note">
                  この URL を使って、ログインなしでクイズにアクセスできます。
                </p>
              </div>
            </div>
          )}
        </div>


        <div className="questions-section">
          <div className="questions-header">
            <h3>質問 ({questions.length})</h3>
            <div className="question-actions">
              <button 
                type="button" 
                onClick={handleAddQuestion}
                className="add-question-btn"
              >
                + 質問を追加
              </button>
              <button 
                type="button" 
                onClick={handleImportQuestions}
                className="import-question-btn"
              >
                📁 JSONからインポート
              </button>
              <button 
                type="button" 
                onClick={handleAiQuizGeneration}
                className="ai-generation-btn"
              >
                🤖 AIでクイズ作成
              </button>
            </div>
          </div>

          {questions.length === 0 ? (
            <div className="no-questions">
              <p>まだ質問が追加されていません</p>
              <p>「質問を追加」ボタンをクリックして質問を作成してください</p>
            </div>
          ) : (
            <>
              <div className="questions-list">
                {currentQuestions.map((question, index) => {
                  const actualIndex = startIndex + index;
                  return (
                    <div key={question.id} className="question-item">
                      <div className="question-header">
                        <span className="question-number">質問 {actualIndex + 1}</span>
                        <div className="question-actions">
                          <button
                            type="button"
                            onClick={() => handleEditQuestion(actualIndex, question.id)}
                            className="edit-btn"
                          >
                            編集
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteQuestion(actualIndex, question.id)}
                            className="delete-btn"
                          >
                            削除
                          </button>
                        </div>
                      </div>
                      <div className="question-preview">
                        <p><strong>質問:</strong> {question.questionText}</p>
                        <p><strong>タイプ:</strong> {question.questionType === 'single' ? '単一選択' : '複数選択'}</p>
                        <p><strong>選択肢:</strong> {question.choices.length}個</p>
                        {question.tags && question.tags.length > 0 && (
                          <p><strong>タグ:</strong> {question.tags.join(', ')}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    type="button"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    ← 前へ
                  </button>
                  
                  <div className="pagination-center">
                    <span className="pagination-info">
                      {currentPage} / {totalPages}
                    </span>
                    <form onSubmit={handlePageInputSubmit} className="page-input-form">
                      <input
                        type="number"
                        value={pageInput}
                        onChange={handlePageInputChange}
                        placeholder="ページ番号"
                        min="1"
                        max={totalPages}
                        className="page-input"
                      />
                      <button type="button" onClick={handlePageInputSubmit} className="page-go-btn">
                        移動
                      </button>
                    </form>
                  </div>
                  
                  <button 
                    type="button"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    次へ →
                  </button>
                </div>
              )}
              
              <div className="pagination-summary">
                {questions.length > 0 && (
                  <p>
                    {startIndex + 1} - {Math.min(endIndex, questions.length)} / {questions.length} 件
                  </p>
                )}
              </div>
            </>
          )}
        </div>


        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-btn">
            キャンセル
          </button>
          <button 
            type="button" 
            onClick={() => setShowDeleteConfirm(true)}
            className="delete-quiz-btn"
          >
            クイズを削除
          </button>
          <button 
            type="submit" 
            className="save-btn"
            disabled={saving}
          >
            {saving ? '保存中...' : 'クイズを保存'}
          </button>
        </div>
        
        {showDeleteConfirm && (
          <div className="delete-confirm-modal">
            <div className="modal-content">
              <h3>クイズを削除</h3>
              <p>「{quiz.title}」を削除しますか？</p>
              <p>この操作は取り消せません。</p>
              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="cancel-btn"
                >
                  キャンセル
                </button>
                <button 
                  type="button" 
                  onClick={handleDeleteQuiz}
                  className="delete-btn"
                >
                  削除
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default EditQuizForm;
